// The one interface every screen reads through. Three implementations: DemoSource (a published-snapshot file, no network),
// SupabaseSource (the live tables/views of DATA_CONTRACT §5.2 with Realtime) and LocalSource (the local desk's gateway, ADR-0043). Screens never know which one they have.
//
// Contract for implementers:
// - Reads return engine read models only. Nothing here computes a status (rule 9).
// - `submit` inserts ONE inbox row naming one catalogue command; the engine runs it through its gates. The browser never
//   supplies an actor, a role or a date; the database stamps the person, the engine stamps the act.
// - Row-level security decides what a person sees; the demo source imitates the same visibility rules so screens can be
//   tested per role.
import type {
  Action, Activity, AgentRun, ChangeEvent, CommandSpec, EngagementHeader, EngagementModel, EngagementTables, FirmModel, FirmModelKind, Heartbeat,
  InboxResult, InboxRow, Member, Message, PbcRequest, Person, ResearchFinding, Role, SearchEntry, SubmitRequest, Thread, ThreadAttachment, Topic, Upload,
} from "./types";

export interface DataSource {
  /** demo: a published snapshot file; supabase: the cloud; local: the desk on the engine's own machine (data/local.ts). */
  readonly mode: "demo" | "supabase" | "local";

  // ── identity ──
  member(): Promise<Member | null>;
  /** Supabase: email a one-time sign-in link. Demo: sign in as the named role. */
  signIn(emailOrRole: string): Promise<void>;
  /** Supabase only: sign in with the same email and password as the firm's website (one shared login). */
  signInWithPassword?(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  /** Demo only: the synthetic people you can sign in as. */
  demoIdentities?(): Member[];

  // ── firm ──
  people(): Promise<Person[]>;
  catalogue(): Promise<CommandSpec[]>;
  heartbeat(): Promise<Heartbeat[]>;
  /** A firm read model document, or null when it is not published or this person's role may not read it. */
  firmModel(kind: FirmModelKind): Promise<FirmModel | null>;
  /** Research readings through the blind view: a first reading's excerpt and date only for its reader until confirmed. */
  researchFindings(): Promise<ResearchFinding[]>;

  // ── engagements ──
  engagements(): Promise<EngagementHeader[]>;
  engagement(id: string): Promise<EngagementModel>;
  /** Any published engagement table, typed by name (see EngagementTables). */
  table<K extends keyof EngagementTables>(engagementId: string, name: K): Promise<EngagementTables[K][]>;
  /** Actions addressed to this person (work.next --person) across every engagement they can see, plus firm actions. */
  myActions(): Promise<Action[]>;
  /** Agent runs that have not ended, across engagements. */
  activeRuns(): Promise<AgentRun[]>;
  /** A short-lived signed URL for a rendered workpaper in the private bucket (`wp_files.storage_path`), or null when there is
   * none to give (demo, not uploaded, or storage refused it). The local file stays the system of record. */
  fileUrl(engagementId: string, storagePath: string): Promise<string | null>;
  /** Entries for the command palette: engagements, workpapers, folders. */
  searchIndex(): Promise<SearchEntry[]>;

  // ── inbox ──
  /** This person's recent requests and their outcomes. */
  inbox(): Promise<InboxRow[]>;
  submit(req: SubmitRequest): Promise<InboxRow>;
  /** Result of a read-mode request (`inbox_results`), when the engine has written it. */
  readResult(inboxId: string): Promise<InboxResult | null>;
  /** Realtime: called on every change of the topic; returns the unsubscribe function. */
  subscribe(topic: Topic, cb: (e: ChangeEvent) => void): () => void;

  // ── live pages (ADR-0032; data/live.ts) ──
  /** When the engine last published each of these tables for the engagement (`sync_state.at`, cloud 0009), or null when that is not
   * known here (the migration is not applied, or the demo has no publisher) — the page then polls. */
  publishedAt?(engagementId: string, tables: string[]): Promise<Record<string, string> | null>;
  /** Called when the engine publishes one of these tables for the engagement (Realtime on `sync_state`); returns the unsubscribe. */
  watchTables?(engagementId: string, tables: string[], cb: () => void): () => void;
  /** One page of the activity log, newest first: entries before `beforeSeq` (the newest when null), at most `limit`. */
  activityPage?(engagementId: string, beforeSeq: number | null, limit: number): Promise<{ rows: Activity[]; more: boolean }>;

  // ── client portal ──
  clientRequests(): Promise<Array<PbcRequest & { engagement: string }>>;
  clientUploads(): Promise<Upload[]>;
  /** A file for a sent request, through the Worker (it checks the bytes; RLS decides). `onProgress` gets 0..1 while it uploads. */
  upload(engagementId: string, requestId: number, file: File, onProgress?: (fraction: number) => void): Promise<Upload>;
  /** A file attached to an open question or follow-up thread, through the Worker (`client_attachments`). */
  attach(engagementId: string, threadId: string, file: File, onProgress?: (fraction: number) => void): Promise<ThreadAttachment>;
  /** Thread attachments this person may see (a client: their own; the team: every one of the engagement). */
  clientAttachments(): Promise<ThreadAttachment[]>;
  clientThreads(): Promise<Thread[]>;
  threadMessages(engagementId: string, threadId: string): Promise<Message[]>;
  /** A client's own message into an open, client-visible thread (RLS + trigger stamp the author). `intent: "na_request"` asks the
   * firm to find the item not applicable; the body is the client's reason. */
  postClientMessage(engagementId: string, threadId: string, body: string, intent?: "message" | "na_request"): Promise<Message>;
  /** The client's display name of each engagement they can see (`client_engagements`). */
  clientEngagements(): Promise<Array<{ id: string; name: string; fye: string | null }>>;
}

export const ROLE_RANK: Record<Role | "eqr", number> = { client: 0, staff: 1, manager: 2, eqr: 2, partner: 3 };

/** Firm read models a role may read (UI_DATA §2.3: `partners_only` kinds; the outbox holds emails → managers). The database
 * enforces this (and each published row says `partners_only`); the UI only explains it. */
export const FIRM_MODEL_MIN_ROLE: Partial<Record<FirmModelKind, Role>> = {
  owner_queue: "partner", learning: "partner", firm_healing: "partner", quality: "partner", security: "partner", time_firm: "partner", outbox: "manager",
};
export function mayReadFirmModel(role: Role, kind: FirmModelKind): boolean {
  if (role === "client") return false;
  return ROLE_RANK[role] >= ROLE_RANK[FIRM_MODEL_MIN_ROLE[kind] ?? "staff"];
}

/** A fresh idempotency key: keep it for the life of one intended act, so a retry or a double click is not a second act. */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return "k-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2);
}
